/**
 * Shared utilities for admin list pages (UsersPage, AuditLogsPage, ReportSchedulerPage).
 * Extracted to reduce code duplication across these pages.
 * Do NOT edit without understanding the downstream consumers.
 */
import React from 'react';
import {
  TableSortLabel,
  TablePagination,
  Alert,
  Snackbar,
} from '@mui/material';

// ---------------------------------------------------------------------------
// useSortPagination hook – shared sort + pagination state/handlers
// ---------------------------------------------------------------------------

export interface SortPaginationState {
  page: number;
  rowsPerPage: number;
  sortBy: string;
  sortDir: 'asc' | 'desc';
}

export interface SortPaginationHandlers {
  handleChangePage: (_event: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSort: (field: string) => void;
}

/**
 * Returns sort/pagination handlers that call the provided setters.
 * `onPageChange` is called with the new page number so callers can
 * trigger a data fetch (used by ReportSchedulerPage which fetches imperatively).
 */
export function makeSortPaginationHandlers(
  state: SortPaginationState,
  setPage: React.Dispatch<React.SetStateAction<number>>,
  setRowsPerPage: React.Dispatch<React.SetStateAction<number>>,
  setSortBy: React.Dispatch<React.SetStateAction<string>>,
  setSortDir: React.Dispatch<React.SetStateAction<'asc' | 'desc'>>,
  onPageChange?: (newPage: number, newSize: number) => void,
): SortPaginationHandlers {
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    onPageChange?.(newPage, state.rowsPerPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = Number.parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
    onPageChange?.(0, newSize);
  };

  const handleSort = (field: string) => {
    if (state.sortBy === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
    setPage(0);
  };

  return { handleChangePage, handleChangeRowsPerPage, handleSort };
}

// ---------------------------------------------------------------------------
// SortableHeaderCell – renders a TableSortLabel for a column header
// ---------------------------------------------------------------------------

interface SortableHeaderCellProps {
  field: string;
  label: string;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  onSort: (field: string) => void;
}

/**
 * Renders a <TableSortLabel> for use inside a <TableCell>.
 * Identical logic previously duplicated in all three pages.
 */
export const SortableHeaderCell: React.FC<SortableHeaderCellProps> = ({
  field,
  label,
  sortBy,
  sortDir,
  onSort,
}) => (
  <TableSortLabel
    active={sortBy === field}
    direction={sortBy === field ? sortDir : 'asc'}
    onClick={() => onSort(field)}
  >
    {label}
  </TableSortLabel>
);

// ---------------------------------------------------------------------------
// AdminTablePagination – shared TablePagination wrapper
// ---------------------------------------------------------------------------

interface AdminTablePaginationProps {
  count: number;
  page: number;
  rowsPerPage: number;
  rowsPerPageOptions?: number[];
  onPageChange: (_event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * Renders a <TablePagination> component with the standard admin page settings.
 * Only shown when `loading` is false (callers wrap with {!loading && <…/>}).
 */
export const AdminTablePagination: React.FC<AdminTablePaginationProps> = ({
  count,
  page,
  rowsPerPage,
  rowsPerPageOptions = [10, 25, 50],
  onPageChange,
  onRowsPerPageChange,
}) => (
  <TablePagination
    rowsPerPageOptions={rowsPerPageOptions}
    component="div"
    count={count}
    rowsPerPage={rowsPerPage}
    page={page}
    onPageChange={onPageChange}
    onRowsPerPageChange={onRowsPerPageChange}
  />
);

// ---------------------------------------------------------------------------
// StatusSnackbars – shared success/error Snackbar pair
// ---------------------------------------------------------------------------

interface StatusSnackbarsProps {
  success: string | null;
  error: string | null;
  onClearSuccess: () => void;
  onClearError: () => void;
}

/**
 * Renders the standard success + error <Snackbar> pair used by admin pages.
 */
export const StatusSnackbars: React.FC<StatusSnackbarsProps> = ({
  success,
  error,
  onClearSuccess,
  onClearError,
}) => (
  <>
    <Snackbar open={!!success} autoHideDuration={6000} onClose={onClearSuccess}>
      <Alert severity="success" onClose={onClearSuccess}>
        {success}
      </Alert>
    </Snackbar>
    <Snackbar open={!!error} autoHideDuration={6000} onClose={onClearError}>
      <Alert severity="error" onClose={onClearError}>
        {error}
      </Alert>
    </Snackbar>
  </>
);
